import { FeatureLesson } from '../learning/FeatureLesson';
import { useId } from 'react';
import { SlideOver } from '../ui/SlideOver';
import { Toggle } from '../ui/Toggle';
import { FeatureGateBadge } from '../ui/FeatureGateBadge';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { ButtonContent } from '../ui/ButtonContent';
import LanguageSelector from '../LanguageSelector';
import { toLocalDatetimeString } from '../../lib/admin-utils';
import { validateConversationSettings, type ConversationSettingsValues } from './settings-model';
import { useConversationSettingsAccess } from './useConversationSettingsAccess';

/** Controlled settings shared by creation, editing and the conversation overview. */
interface ConversationSettingsPanelProps {
  /** Whether the settings panel is open. */
  open: boolean;
  /** Close or cancel using the host's persistence lifecycle. */
  onClose: () => void;
  /** Current settings, including unsaved builder values. */
  value: ConversationSettingsValues;
  /** Apply only changed settings without replacing unrelated configuration. */
  onChange: (patch: Partial<ConversationSettingsValues>) => void;
  /** Authored questions for call-limit validation. */
  fields: unknown;
  /** Existing responses constrain the response cap. */
  responseCount?: number;
  /** Omit when the host saves settings with the surrounding conversation. */
  onSave?: () => void;
  /** Whether the host is persisting settings. */
  saving?: boolean;
  /** Persistence failure owned by the host. */
  error?: string | null;
}

const inputClasses = 'w-full min-w-0 bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-subtle-foreground focus:border-volt focus:outline-none focus:ring-1 focus:ring-volt transition-colors';
const labelClasses = 'block text-sm font-medium text-muted-foreground mb-2';

/** Keep input width and focus stable when the clear action disappears. */
function ClearSettingButton({ visible, onClear }: { visible: boolean; onClear: () => void }) {
  return <button type="button" onClick={onClear} disabled={!visible} tabIndex={visible ? undefined : -1} aria-hidden={!visible}
    className={`shrink-0 px-4 py-3 text-sm text-muted-foreground hover:text-foreground bg-background border border-border rounded-lg transition-[opacity,transform,background-color,color] duration-200 motion-reduce:transition-none ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
    Clear
  </button>;
}

/** The sole settings layout and control implementation for every authoring entry point. */
export function ConversationSettingsPanel({ open, onClose, value, onChange, fields, responseCount = 0, onSave, saving = false, error = null }: ConversationSettingsPanelProps) {
  const id = useId();
  const { can, languagePolicy } = useConversationSettingsAccess();
  const validation = validateConversationSettings(value, { fields, responseCount, can });
  const canVideo = can('video_interviews');
  const videoEnabled = value.video_enabled && canVideo;
  const completionAction = value.completion_action === 'redirect' && can('completion_redirect') ? 'redirect' : 'message';

  return <SlideOver open={open} onClose={onClose} title="Advanced settings" footer={onSave ? (
    <div className="flex gap-3">
      <button type="button" aria-busy={saving} onClick={onSave} disabled={saving || validation.invalid}
        className="flex-1 py-3 button-accent font-bold rounded-lg transition-[background-color,color,opacity] duration-200 motion-reduce:transition-none disabled:opacity-50">
        <ButtonContent loading={saving}>Save Settings</ButtonContent>
      </button>
      <button type="button" onClick={onClose} className="flex-1 py-3 bg-surface-raised text-muted-foreground font-bold rounded-lg hover:bg-surface-hover transition-colors">Cancel</button>
    </div>
  ) : undefined}>
    <AnimatedPresence show={!!error}>
      <div className="pb-5"><div className="bg-wash-danger rounded-lg p-3"><p className="text-sm text-danger-soft"><AnimatedLabel text={error ?? ''} multiline /></p></div></div>
    </AnimatedPresence>
    <div className="space-y-8">
      <div>
        <FeatureLesson lesson="calls" ready={open} engaged={value.conversation_config.callsEnabled}><Toggle label="Allow calls" enabled={value.conversation_config.callsEnabled}
          onChange={callsEnabled => onChange({ conversation_config: { ...value.conversation_config, callsEnabled } })}
          disabled={!value.conversation_config.callsEnabled && !can('audio_interviews') && !canVideo}
          labelSuffix={<FeatureGateBadge feature="audio_interviews" />} /></FeatureLesson>
        <AnimatedPresence show={!!validation.callAccessIssue}><p role="alert" className="pt-4 text-sm text-danger"><AnimatedLabel text={validation.callAccessIssue ?? ''} multiline /></p></AnimatedPresence>
        <AnimatedPresence show={!!validation.callQuestionIssue}><p role="alert" className="pt-4 text-sm text-danger"><AnimatedLabel text={validation.callQuestionIssue?.message ?? ''} multiline /></p></AnimatedPresence>
        <AnimatedPresence show={value.conversation_config.callsEnabled}>
          <div role="group" aria-label="Call settings" className="pt-3">
            <p className="text-sm text-muted-foreground">Each call uses 1 credit. Text-only conversations are free.</p>
            <h3 className="text-volt font-bold uppercase tracking-wider text-xs mt-6 mb-3">Format</h3>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" aria-pressed={!videoEnabled} onClick={() => onChange({ video_enabled: false, require_video: false })}
                className={`flex flex-col items-center gap-1 px-3 pt-9 pb-3 rounded-lg border-2 transition-colors ${!videoEnabled ? 'border-volt bg-volt/10' : 'border-border bg-background/50 hover:border-border'}`}>
                <span className="text-sm font-semibold text-foreground">Audio</span>
              </button>
              <div className="relative">
                <button type="button" aria-pressed={videoEnabled} onClick={() => onChange({ video_enabled: true })} disabled={!canVideo}
                  className={`w-full flex flex-col items-center gap-1 px-3 pt-9 pb-3 rounded-lg border-2 transition-colors ${!canVideo ? 'border-border bg-background/50 opacity-60 cursor-not-allowed' : videoEnabled ? 'border-volt bg-volt/10' : 'border-border bg-background/50 hover:border-border'}`}>
                  <span className="text-sm font-semibold text-foreground">Video</span>
                </button>
                <div className="absolute top-2 right-2"><FeatureGateBadge feature="video_interviews" /></div>
              </div>
            </div>
            <label className="mt-5 grid gap-2 text-sm text-muted-foreground">Call entry
              <select aria-label="Call entry" className={inputClasses} value={value.conversation_config.callEntry ?? 'offer'}
                onChange={event => onChange({ conversation_config: { ...value.conversation_config, callEntry: event.target.value as NonNullable<ConversationSettingsValues['conversation_config']['callEntry']> } })}>
                <option value="offer">Offer immediately</option><option value="after_first_answer">After first answer</option><option value="call">Start with a call</option>
              </select>
            </label>
            <AnimatedPresence show={videoEnabled || value.require_video}>
              <div className="pt-5"><Toggle enabled={value.require_video} onChange={require_video => onChange({ require_video })}
                label="Require Camera" description="Camera is mandatory. Respondents cannot opt for audio-only." /></div>
            </AnimatedPresence>
            <div className="pt-5"><Toggle enabled={value.screen_share_enabled && can('screen_sharing')} onChange={screen_share_enabled => onChange({ screen_share_enabled })}
              label="Allow Screen Sharing" description="hollie may ask to see the respondent's screen when useful; respondents can also share on their own."
              disabled={!can('screen_sharing')} labelSuffix={<FeatureGateBadge feature="screen_sharing" />} /></div>
            {/* Call languages only. Future text auto-translation needs a separate policy. */}
            <div className="pt-6 grid grid-cols-1 gap-4">
              <div className="relative pt-7">
                <LanguageSelector value={languagePolicy.allowLanguageChoice ? value.enforce_language ?? '' : languagePolicy.enforcedLanguage ?? ''} onChange={enforce_language => onChange({ enforce_language: enforce_language || null })}
                  label="Conversation Language" helperText={languagePolicy.allowLanguageChoice ? 'Lock calls to one language.' : 'Your organization has restricted language choice.'}
                  allowEmpty emptyLabel="Respondent's choice" disabled={!languagePolicy.allowLanguageChoice} />
                <div className="absolute top-0 right-0"><FeatureGateBadge feature="multi_language_interviews" /></div>
              </div>
              <LanguageSelector value={value.output_language} onChange={output_language => onChange({ output_language })} label="Content Language" helperText="Transcript and case study language." />
            </div>
          </div>
        </AnimatedPresence>
      </div>

      <div className="space-y-5">
        <h3 className="text-volt font-bold uppercase tracking-wider text-xs">Response Collection</h3>
        <div>
          <label htmlFor={`${id}-responses`} className={labelClasses}>Max Responses</label>
          <div className="flex items-center gap-3">
            <input id={`${id}-responses`} type="number" min={validation.minResponses} value={value.max_responses ?? ''}
              onChange={event => onChange({ max_responses: event.target.value === '' ? null : Number(event.target.value) })}
              placeholder="Unlimited" className={`flex-1 ${inputClasses} ${validation.hasMaxResponsesError ? 'border-danger focus:border-danger' : ''}`} />
            <ClearSettingButton visible={value.max_responses !== null} onClear={() => onChange({ max_responses: null })} />
          </div>
          <p className="text-xs text-subtle-foreground mt-1">Leave empty for unlimited responses.</p>
          <AnimatedPresence show={validation.hasMaxResponsesError}>
            <p className="text-xs text-danger mt-1">Cannot be less than {validation.minResponses}: {responseCount} {responseCount === 1 ? 'response has' : 'responses have'} already been collected.</p>
          </AnimatedPresence>
        </div>
        <Toggle enabled={value.allow_duplicate_emails} onChange={allow_duplicate_emails => onChange({ allow_duplicate_emails })} label="Allow Duplicate Emails" description="Allow same email to submit multiple responses" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label id={`${id}-completion`} className="block text-sm font-medium text-muted-foreground">On submission</label>
          <FeatureGateBadge feature="completion_redirect" />
        </div>
        <div role="radiogroup" aria-labelledby={`${id}-completion`} className="grid grid-cols-2 gap-2">
          {([{ value: 'message', label: 'Show message', enabled: true }, { value: 'redirect', label: 'Redirect', enabled: can('completion_redirect') }] as const).map(option => (
            <label key={option.value} className={`text-center text-xs font-bold rounded-lg py-2 px-2 border transition-colors ${!option.enabled ? 'bg-background/40 text-subtle-foreground border-border cursor-not-allowed' : completionAction === option.value ? 'bg-volt text-accent-foreground border-volt cursor-pointer' : 'bg-background text-muted-foreground border-border hover:border-border cursor-pointer'}`}>
              <input type="radio" name={`${id}-completion-action`} value={option.value} checked={completionAction === option.value} disabled={!option.enabled}
                onChange={() => onChange({ completion_action: option.value })} className="sr-only" />{option.label}
            </label>
          ))}
        </div>
        <AnimatedPresence show={completionAction === 'message'}>
          <textarea value={value.completion_message ?? ''} onChange={event => onChange({ completion_message: event.target.value })}
            placeholder="Your response has been submitted. Thank you for your time." rows={3} className={`mt-3 resize-none ${inputClasses}`} />
        </AnimatedPresence>
        <AnimatedPresence show={completionAction === 'redirect'}>
          <input type="url" value={value.completion_redirect_url ?? ''} onChange={event => onChange({ completion_redirect_url: event.target.value })}
            placeholder="https://your-site.com/thanks" className={`mt-3 ${inputClasses} ${validation.hasRedirectError ? 'border-danger focus:border-danger' : ''}`} />
        </AnimatedPresence>
      </div>

      <div className="space-y-5">
        <h3 className="text-volt font-bold uppercase tracking-wider text-xs">Schedule</h3>
        {([{ key: 'opens_at', label: 'Opens At', hint: 'Leave empty to open immediately.' }, { key: 'closes_at', label: 'Closes At', hint: 'Leave empty for no auto-close.' }] as const).map(setting => (
          <div key={setting.key}>
            <label htmlFor={`${id}-${setting.key}`} className={labelClasses}>{setting.label}</label>
            <div className="flex items-center gap-3">
              <input id={`${id}-${setting.key}`} type="datetime-local" value={value[setting.key] ? toLocalDatetimeString(value[setting.key] ?? '') : ''}
                onChange={event => onChange({ [setting.key]: event.target.value ? new Date(event.target.value).toISOString() : null })}
                className={`flex-1 ${inputClasses} dark:[color-scheme:dark]`} />
              <ClearSettingButton visible={!!value[setting.key]} onClear={() => onChange({ [setting.key]: null })} />
            </div>
            <p className="text-xs text-subtle-foreground mt-1">{setting.hint}</p>
          </div>
        ))}
        <AnimatedPresence show={validation.hasDateError}><p className="text-xs text-danger">Open date must be before close date.</p></AnimatedPresence>
      </div>
    </div>
  </SlideOver>;
}
